/**
 * Where to send someone after sign-in (used by /, /login, /setup and /verify).
 *
 *   employees who have not verified their account  -> /verify/ (?next= kept)
 *   a safe `next` path                              -> that path
 *   everyone else                                   -> /home/ (design/salesforce/PLAN.md 1.4)
 *
 * A `next` path (set by RequireAuth as /login/?next=<path>) wins when it is a
 * safe in-app path (lib/next-path safeNextPath). Whether the user may open
 * it is checked by the page itself (the (crm) layout's RequireAuth and the
 * per-page guards), which show "No access" with a Home button otherwise.
 */
import { VERIFY_PATH } from "@/lib/auth";
import { safeNextPath } from "@/lib/next-path";

/** Where everyone lands after sign-in. */
export const HOME_PATH = "/home/";

/** Wide enough for the desktop Lightning shell (app rail + content). */
export const DASHBOARD_MEDIA_QUERY = "(min-width: 1024px)";

export function isWideScreen(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(DASHBOARD_MEDIA_QUERY).matches;
}

/** Reads ?next= from the current URL (client only). */
export function nextFromLocation(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return safeNextPath(new URLSearchParams(window.location.search).get("next"));
  } catch {
    return null;
  }
}

export function postLoginPath(opts: {
  /** Kept for the callers; the destination no longer depends on it (everyone lands on /home/). */
  canUseDashboard?: boolean;
  /** useAuth().needsVerification: go to /verify/ first, keeping `next` for afterwards. */
  needsVerification?: boolean;
  next?: string | null;
}): string {
  const next = safeNextPath(opts.next);
  if (opts.needsVerification) return next ? `${VERIFY_PATH}?next=${encodeURIComponent(next)}` : VERIFY_PATH;
  return next ?? HOME_PATH;
}
